import React, { useEffect, useState } from 'react';
import { useServiceOS } from '../hooks/useServiceOS';
import { BACKEND_URL } from '../../config';
import {
  Globe,
  Copy,
  CheckCircle,
  Link as LinkIcon,
  Code,
  ChatCircleDots,
  Eye,
  ToggleLeft,
  ToggleRight,
} from '@phosphor-icons/react';

export default function SOSDeploySection() {
  const { getDeployment, createDeployment } = useServiceOS();
  const [deploy, setDeploy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');
  const [brandColor, setBrandColor] = useState('#14F195');
  const [introText, setIntroText] = useState('');

  useEffect(() => {
    getDeployment()
      .then(res => {
        if (res.deployment) {
          setDeploy(res.deployment);
          setBrandColor(res.deployment.brand_color || '#14F195');
          setIntroText(res.deployment.intro_text || '');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getDeployment]);

  const handleSaveOrCreate = async () => {
    setSaving(true);
    try {
      const res = await createDeployment({
        brand_color: brandColor,
        intro_text: introText,
        is_active: deploy?.is_active !== false,
      });
      setDeploy(res.deployment);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!deploy) return;
    setSaving(true);
    try {
      const res = await createDeployment({
        brand_color: brandColor,
        intro_text: introText,
        is_active: !deploy.is_active,
      });
      setDeploy(res.deployment);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return null;

  // Build URLs and snippets
  const origin = window.location.origin;
  const shareUrl = deploy ? `${origin}/enquiry/${deploy.public_key}` : '';
  const inlineSnippet = deploy ? `<iframe src="${shareUrl}" style="width:100%;min-height:600px;border:none;border-radius:12px;" title="Enquiry Form"></iframe>` : '';
  const bubbleSnippet = deploy ? `<script>
(function(){
  var k="${deploy.public_key}",c="${brandColor}",u="${shareUrl}";
  var b=document.createElement("div");
  b.innerHTML='<div id="sos-bubble" style="position:fixed;bottom:24px;right:24px;z-index:9999;">'
    +'<button onclick="document.getElementById(\\'sos-frame\\').style.display=document.getElementById(\\'sos-frame\\').style.display===\\'none\\'?\\'block\\':\\'none\\'" style="width:56px;height:56px;border-radius:50%;background:'+c+';border:none;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">'
    +'<svg width="24" height="24" fill="white" viewBox="0 0 256 256"><path d="M216,48H40A16,16,0,0,0,24,64V224a15.85,15.85,0,0,0,9.24,14.5A16.13,16.13,0,0,0,40,240a15.89,15.89,0,0,0,10.25-3.78l.09-.07L83,208H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48Z"></path></svg>'
    +'</button>'
    +'<iframe id="sos-frame" src="'+u+'" style="display:none;position:fixed;bottom:96px;right:24px;width:400px;height:600px;border:none;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.4);z-index:9999;"></iframe>'
    +'</div>';
  document.body.appendChild(b);
})();
</script>` : '';

  return (
    <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-6 space-y-5" data-testid="deploy-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe size={18} className="text-[#6366F1]" weight="duotone" />
          <h2 className="text-sm font-semibold text-white">Website Enquiry Form</h2>
        </div>
        {deploy && (
          <button
            onClick={handleToggle}
            disabled={saving}
            className="flex items-center gap-1.5 text-xs"
            data-testid="deploy-toggle"
          >
            {deploy.is_active ? (
              <><ToggleRight size={20} className="text-[#22C55E]" weight="fill" /> <span className="text-[#22C55E]">Active</span></>
            ) : (
              <><ToggleLeft size={20} className="text-[#EF4444]" weight="fill" /> <span className="text-[#EF4444]">Inactive</span></>
            )}
          </button>
        )}
      </div>
      <p className="text-xs text-[#8B8FA3]">
        Add a lead capture form to your website. Customer enquiries are processed automatically and appear in your Leads.
      </p>

      {/* Config */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-[#8B8FA3] mb-1">Brand colour</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={brandColor}
              onChange={e => setBrandColor(e.target.value)}
              className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent"
              data-testid="deploy-brand-color"
            />
            <input
              type="text"
              value={brandColor}
              onChange={e => setBrandColor(e.target.value)}
              className="flex-1 px-3 py-1.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-xs text-white font-mono"
              data-testid="deploy-brand-color-text"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-[#8B8FA3] mb-1">Intro text <span className="text-[#555]">(optional)</span></label>
          <input
            value={introText}
            onChange={e => setIntroText(e.target.value)}
            placeholder="e.g. Need a plumber? Tell us what you need."
            className="w-full px-3 py-1.5 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-xs text-white placeholder:text-[#555]"
            data-testid="deploy-intro-text"
          />
        </div>
      </div>

      <button
        onClick={handleSaveOrCreate}
        disabled={saving}
        className="px-4 py-2 bg-[#6366F1] text-white rounded-lg text-xs font-semibold hover:bg-[#5558E6] disabled:opacity-40 transition-colors"
        data-testid="deploy-save-btn"
      >
        {saving ? 'Saving...' : deploy ? 'Update settings' : 'Enable enquiry form'}
      </button>

      {/* Deployment snippets */}
      {deploy && (
        <div className="space-y-4 pt-2 border-t border-[#1E1F2E]">
          {/* Share Link */}
          <div data-testid="deploy-share-link">
            <div className="flex items-center gap-2 mb-2">
              <LinkIcon size={14} className="text-[#8B8FA3]" />
              <span className="text-xs font-medium text-[#C4C9D4]">Share Link</span>
            </div>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg">
                <code className="text-xs text-[#6366F1] font-mono break-all" data-testid="share-link-url">{shareUrl}</code>
              </div>
              <button
                onClick={() => copyText(shareUrl, 'share')}
                className={`flex items-center gap-1 px-3 rounded-lg text-xs font-medium shrink-0 ${copied === 'share' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#6366F1]/10 text-[#6366F1] hover:bg-[#6366F1]/20'}`}
                data-testid="copy-share-link"
              >
                {copied === 'share' ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 bg-[#1E1F2E] rounded-lg text-xs text-[#8B8FA3] hover:text-white" data-testid="preview-share-link">
                <Eye size={12} />
              </a>
            </div>
          </div>

          {/* Inline Embed */}
          <div data-testid="deploy-inline-embed">
            <div className="flex items-center gap-2 mb-2">
              <Code size={14} className="text-[#8B8FA3]" />
              <span className="text-xs font-medium text-[#C4C9D4]">Inline Embed</span>
            </div>
            <div className="flex items-start gap-2">
              <pre className="flex-1 px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-[10px] text-[#8B8FA3] font-mono overflow-x-auto whitespace-pre-wrap">{inlineSnippet}</pre>
              <button
                onClick={() => copyText(inlineSnippet, 'inline')}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium shrink-0 ${copied === 'inline' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#6366F1]/10 text-[#6366F1] hover:bg-[#6366F1]/20'}`}
                data-testid="copy-inline-embed"
              >
                {copied === 'inline' ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
          </div>

          {/* Chat Bubble */}
          <div data-testid="deploy-chat-bubble">
            <div className="flex items-center gap-2 mb-2">
              <ChatCircleDots size={14} className="text-[#8B8FA3]" />
              <span className="text-xs font-medium text-[#C4C9D4]">Chat Bubble</span>
            </div>
            <div className="flex items-start gap-2">
              <pre className="flex-1 px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-[10px] text-[#8B8FA3] font-mono overflow-x-auto whitespace-pre-wrap max-h-32">{bubbleSnippet}</pre>
              <button
                onClick={() => copyText(bubbleSnippet, 'bubble')}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium shrink-0 ${copied === 'bubble' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#6366F1]/10 text-[#6366F1] hover:bg-[#6366F1]/20'}`}
                data-testid="copy-chat-bubble"
              >
                {copied === 'bubble' ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
